import { create } from 'zustand';

export type Ticker = string;

export type ExposureType = 'gex' | 'vex';

export interface ExposureCell {
  raw: number;
  formatted: string;
}

export interface ExposureRow {
  strike: number;
  cells: Record<string, ExposureCell>;
  total: ExposureCell;
  maxOI: boolean;
  maxVolume: boolean;
}

export interface HeatmapData {
  ticker: string;
  type: ExposureType;
  spotPrice: number;
  expirations: string[];
  rows: ExposureRow[];
  timestamp: string;
}

export interface HeatmapEnvelope {
  ticker: Ticker;
  type: ExposureType;
  data: HeatmapData;
  updatedAt: string;
}

type ConnectionStatus = 'connecting' | 'connected' | 'retrying';
export type BootstrapStatus = 'pending' | 'complete' | 'failed';
type HeatmapKey = `${Ticker}:${ExposureType}`;

interface HeatmapStore {
  heatmaps: Partial<Record<HeatmapKey, HeatmapEnvelope>>;
  connectionStatus: ConnectionStatus;
  bootstrapStatus: BootstrapStatus;
  bootstrapHeatmapCount: number | null;
  upsertHeatmap: (heatmap: HeatmapEnvelope) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setBootstrapStatus: (status: BootstrapStatus, heatmapCount?: number | null) => void;
}

export function getHeatmapKey(ticker: Ticker, type: ExposureType): HeatmapKey {
  return `${ticker}:${type}`;
}

function getUpdatedAtMs(updatedAt: string): number {
  const parsed = Date.parse(updatedAt);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function shouldReplaceHeatmap(
  existing: HeatmapEnvelope | undefined,
  incoming: HeatmapEnvelope,
): boolean {
  if (!existing) {
    return true;
  }

  return getUpdatedAtMs(incoming.updatedAt) >= getUpdatedAtMs(existing.updatedAt);
}

export const useHeatmapStore = create<HeatmapStore>((set) => ({
  heatmaps: {},
  connectionStatus: 'connecting',
  bootstrapStatus: 'pending',
  bootstrapHeatmapCount: null,
  upsertHeatmap: (heatmap) =>
    set((state) => {
      const heatmapKey = getHeatmapKey(heatmap.ticker, heatmap.type);

      return {
        heatmaps: {
          ...state.heatmaps,
          ...(shouldReplaceHeatmap(state.heatmaps[heatmapKey], heatmap)
            ? { [heatmapKey]: heatmap }
            : {}),
        },
      };
    }),
  setConnectionStatus: (connectionStatus) => set({ connectionStatus }),
  setBootstrapStatus: (bootstrapStatus, bootstrapHeatmapCount = null) =>
    set({ bootstrapStatus, bootstrapHeatmapCount }),
}));
